import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/bporh_admissoes.css';

// Página de COLABORADORES do BPO RH — consulta geral da carteira (espelho
// Convenia): todos os colaboradores de todas as empresas, com filtros por
// empresa, cargo, departamento, gestor, vínculo e situação. Clicando num
// colaborador abre o resumo completo: cadastro, benefícios, férias, histórico
// salarial, afastamentos, dependentes e a linha do tempo de tudo que
// aconteceu com ele na empresa.

const SITUACOES = {
  ativo: { rotulo: 'Ativo', bg: '#e6f7ef', cor: '#116b47', borda: '#1fa971', icone: 'fa-circle-check' },
  em_experiencia: { rotulo: 'Em experiência', bg: '#fff5da', cor: '#8a6d00', borda: '#f1be48', icone: 'fa-hourglass-half' },
  desligado: { rotulo: 'Desligado', bg: '#fdeaec', cor: '#a11d2b', borda: '#dc3545', icone: 'fa-user-minus' },
};

// ícone e cor de cada tipo de evento da linha do tempo
const TIPO_EVENTO = {
  admissao: { icone: 'fa-user-plus', cor: '#1fa971' },
  exame: { icone: 'fa-notes-medical', cor: '#00b5cc' },
  experiencia: { icone: 'fa-flag-checkered', cor: '#f1be48' },
  salario: { icone: 'fa-money-bill-trend-up', cor: '#8b5cf6' },
  ferias: { icone: 'fa-umbrella-beach', cor: '#0a6ea1' },
  afastamento: { icone: 'fa-notes-medical', cor: '#f97316' },
  aviso: { icone: 'fa-envelope', cor: '#6c757d' },
  desligamento: { icone: 'fa-user-minus', cor: '#dc3545' },
};

const nomeCompleto = (c) =>
  [c.nome, c.sobrenome].filter(Boolean).join(' ') || c.nome_social || '';   // nome completo; social só de reserva

const fmtBRL = (v) => (v == null ? '—'
  : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

const BadgeSituacao = ({ s }) => {
  const cfg = SITUACOES[s] || SITUACOES.ativo;
  return (
    <span className="badge fw-semibold"
      style={{ backgroundColor: cfg.bg, color: cfg.cor, border: `1.5px solid ${cfg.borda}` }}>
      <i className={`fas ${cfg.icone} me-1`}></i>{cfg.rotulo}
    </span>
  );
};

// campo do cadastro no modal (rótulo em cima, valor embaixo)
const Campo = ({ rotulo, valor }) => (
  <div className="col-6 col-md-4 col-lg-3">
    <div className="small text-muted">{rotulo}</div>
    <div className="fw-semibold" style={{ fontSize: '.9rem' }}>{valor || '—'}</div>
  </div>
);

const BpoRhColaboradores = () => {
  const API = process.env.REACT_APP_API_BASE;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [colaboradores, setColaboradores] = useState([]);
  const [painel, setPainel] = useState({ total: 0, ativos: 0, em_experiencia: 0, desligados: 0 });
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/bporh/colaboradores-geral/`, { headers: headers() })
      .then((res) => {
        setColaboradores(res.data.colaboradores || []);
        setPainel(res.data.painel || {});
      })
      .catch((error) => setMensagem(`${error.response?.data?.erro || 'Não foi possível carregar os colaboradores'}`))
      .finally(() => setCarregando(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── filtros (client-side: a carteira inteira cabe no navegador) ───
  const [busca, setBusca] = useState('');
  const [fEmpresa, setFEmpresa] = useState('');
  const [fCargo, setFCargo] = useState('');
  const [fDepto, setFDepto] = useState('');
  const [fGestor, setFGestor] = useState('');
  const [fVinculo, setFVinculo] = useState('');
  const [fSituacao, setFSituacao] = useState('ativos');   // padrão: esconde desligados

  const distintos = (campo, base) => [...new Set(base.map(c => c[campo]).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b));

  // as opções de cada select respeitam o filtro de empresa (cargos daquela
  // empresa, gestores daquela empresa...)
  const baseEmpresa = useMemo(
    () => (fEmpresa ? colaboradores.filter(c => c.cliente === fEmpresa) : colaboradores),
    [colaboradores, fEmpresa]);
  const opcoes = useMemo(() => ({
    empresas: distintos('cliente', colaboradores),
    cargos: distintos('cargo', baseEmpresa),
    deptos: distintos('departamento', baseEmpresa),
    gestores: distintos('gestor', baseEmpresa),
    vinculos: distintos('vinculo', baseEmpresa),
  }), [colaboradores, baseEmpresa]);

  const filtrados = useMemo(() => {
    const b = busca.trim().toLowerCase();
    return colaboradores.filter(c => {
      if (fEmpresa && c.cliente !== fEmpresa) return false;
      if (fCargo && c.cargo !== fCargo) return false;
      if (fDepto && c.departamento !== fDepto) return false;
      if (fGestor && c.gestor !== fGestor) return false;
      if (fVinculo && c.vinculo !== fVinculo) return false;
      if (fSituacao === 'ativos' && c.situacao === 'desligado') return false;
      if (fSituacao && fSituacao !== 'ativos' && c.situacao !== fSituacao) return false;
      if (b && ![nomeCompleto(c), c.cliente, c.cargo, c.departamento, c.gestor,
        c.vinculo, c.email].filter(Boolean).join(' ').toLowerCase().includes(b)) return false;
      return true;
    });
  }, [colaboradores, busca, fEmpresa, fCargo, fDepto, fGestor, fVinculo, fSituacao]);

  const limparFiltros = () => {
    setBusca(''); setFEmpresa(''); setFCargo(''); setFDepto('');
    setFGestor(''); setFVinculo(''); setFSituacao('ativos');
  };

  // ─── resumo do colaborador (modal) ───
  const [resumo, setResumo] = useState(null);   // {item, dados?, carregando}
  const abrirResumo = (c) => {
    setResumo({ item: c, carregando: true });
    axios.get(`${API}/api/bporh/colaborador-resumo/`, {
      params: { cliente_id: c.cliente_id, convenia_id: c.convenia_id },
      headers: headers(),
    })
      .then((res) => setResumo({ item: c, dados: res.data, carregando: false }))
      .catch((error) => {
        setMensagem(`${error.response?.data?.erro || 'Não foi possível montar o resumo'}`);
        setResumo(null);
      });
  };

  const cad = resumo?.dados?.cadastro;

  return (
    <div className="bporh-admissoes-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-address-book header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">BPO RH — Colaboradores</h2>
          </div>
          <span className="text-muted small">
            {filtrados.length} de {painel.total} colaboradores
          </span>
        </div>

        {carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary"></div>
            <p className="text-muted mt-2">Carregando a carteira...</p>
          </div>
        ) : (
          <>
            {/* painel-resumo */}
            <div className="adm-resumo mb-4">
              <div className="adm-kpi">
                <span className="v">{painel.total}</span>
                <span className="k">Total</span>
              </div>
              <div className="adm-kpi">
                <span className="v">{painel.ativos}</span>
                <span className="k">Ativos</span>
              </div>
              <div className="adm-kpi pend">
                <span className="v">{painel.em_experiencia}</span>
                <span className="k">Em experiência</span>
              </div>
              <div className="adm-kpi">
                <span className="v">{painel.desligados}</span>
                <span className="k">Desligados</span>
              </div>
            </div>

            {/* filtros */}
            <div className="row g-2 mb-3">
              <div className="col-md-3">
                <input type="text" className="form-control form-control-sm"
                  placeholder="Nome, cargo, gestor, e-mail..."
                  value={busca} onChange={(e) => setBusca(e.target.value)} />
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={fEmpresa}
                  onChange={(e) => { setFEmpresa(e.target.value); setFCargo(''); setFDepto(''); setFGestor(''); }}>
                  <option value="">Todas as empresas</option>
                  {opcoes.empresas.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={fCargo}
                  onChange={(e) => setFCargo(e.target.value)}>
                  <option value="">Todos os cargos</option>
                  {opcoes.cargos.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={fDepto}
                  onChange={(e) => setFDepto(e.target.value)}>
                  <option value="">Todos os departamentos</option>
                  {opcoes.deptos.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={fGestor}
                  onChange={(e) => setFGestor(e.target.value)}>
                  <option value="">Todos os gestores</option>
                  {opcoes.gestores.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="col-md-1">
                <button className="btn btn-sm btn-outline-secondary w-100" onClick={limparFiltros}
                  title="Limpar todos os filtros">
                  <i className="fas fa-eraser"></i>
                </button>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={fVinculo}
                  onChange={(e) => setFVinculo(e.target.value)}>
                  <option value="">Todos os vínculos</option>
                  {opcoes.vinculos.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="col-md-2">
                <select className="form-select form-select-sm" value={fSituacao}
                  onChange={(e) => setFSituacao(e.target.value)}>
                  <option value="ativos">Ativos (padrão)</option>
                  <option value="em_experiencia">Em experiência</option>
                  <option value="desligado">Desligados</option>
                  <option value="">Todos (inclui desligados)</option>
                </select>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead>
                  <tr>
                    <th><i className="fas fa-user me-2"></i>Colaborador</th>
                    <th><i className="fas fa-building me-2"></i>Empresa</th>
                    <th><i className="fas fa-briefcase me-2"></i>Cargo</th>
                    <th><i className="fas fa-user-tie me-2"></i>Gestor</th>
                    <th><i className="fas fa-file-signature me-2"></i>Vínculo</th>
                    <th><i className="fas fa-calendar-plus me-2"></i>Admissão</th>
                    <th><i className="fas fa-flag me-2"></i>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((c) => (
                    <tr key={`${c.cliente_id}-${c.convenia_id}`} style={{ cursor: 'pointer' }}
                      onClick={() => abrirResumo(c)}>
                      <td className="fw-bold">
                        {nomeCompleto(c)}
                        {c.departamento && (
                          <div className="small text-muted fw-normal">{c.departamento}</div>
                        )}
                      </td>
                      <td>{c.cliente}</td>
                      <td>{c.cargo || '-'}</td>
                      <td>{c.gestor || '-'}</td>
                      <td>{c.vinculo
                        ? <span className="badge bg-light text-muted border">{c.vinculo}</span>
                        : '-'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{c.data_admissao || '-'}</td>
                      <td>
                        <BadgeSituacao s={c.situacao} />
                        {c.situacao === 'desligado' && c.data_desligamento && (
                          <div className="small text-muted">{c.data_desligamento}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filtrados.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        Nenhum colaborador com esses filtros.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ─── MODAL: RESUMO DO COLABORADOR ─── */}
        {resumo && (
          <div className="modal" tabIndex="-1"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content shadow-lg">
                <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                  <div>
                    <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                      <i className="fas fa-id-card me-2"></i>
                      {nomeCompleto(resumo.item)}
                      <span className="ms-2"><BadgeSituacao s={resumo.item.situacao} /></span>
                    </h5>
                    <small style={{ color: '#6c757d' }}>
                      {resumo.item.cliente}
                      {resumo.item.cargo && ` · ${resumo.item.cargo}`}
                      {resumo.item.departamento && ` · ${resumo.item.departamento}`}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setResumo(null)}></button>
                </div>

                <div className="modal-body">
                  {resumo.carregando ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary"></div>
                      <p className="text-muted mt-2 mb-0">Montando o resumo...</p>
                    </div>
                  ) : (
                    <>
                      {/* ── cadastro ── */}
                      <h6 className="fw-bold mb-2" style={{ color: '#00205b' }}>
                        <i className="fas fa-id-badge me-2"></i>Cadastro
                      </h6>
                      <div className="row g-3 mb-4">
                        <Campo rotulo="Admissão" valor={cad.admissao
                          && `${cad.admissao}${cad.dias_de_casa != null ? ` (${cad.dias_de_casa}d de casa)` : ''}`} />
                        <Campo rotulo="Vínculo" valor={cad.vinculo} />
                        <Campo rotulo="Experiência" valor={cad.tipo_experiencia
                          && `${cad.tipo_experiencia}${cad.fim_experiencia ? ` · até ${cad.fim_experiencia}` : ''}`} />
                        <Campo rotulo="Salário" valor={fmtBRL(cad.salario)} />
                        <Campo rotulo="Gestor" valor={cad.gestor} />
                        <Campo rotulo="Time" valor={cad.time} />
                        <Campo rotulo="Centro de custo" valor={cad.centro_custo} />
                        <Campo rotulo="Matrícula" valor={cad.matricula} />
                        <Campo rotulo="CPF" valor={cad.cpf} />
                        <Campo rotulo="PIS" valor={cad.pis} />
                        <Campo rotulo="Nascimento" valor={cad.nascimento} />
                        <Campo rotulo="Cidade" valor={cad.cidade && `${cad.cidade}${cad.uf ? `/${cad.uf}` : ''}`} />
                        <Campo rotulo="E-mail pessoal" valor={cad.email} />
                        <Campo rotulo="E-mail corporativo" valor={cad.email_corporativo} />
                        <Campo rotulo="Celular" valor={cad.celular} />
                        {cad.desligado && <Campo rotulo="Desligamento" valor={cad.data_desligamento} />}
                      </div>

                      {/* ── benefícios ── */}
                      <h6 className="fw-bold mb-2" style={{ color: '#00205b' }}>
                        <i className="fas fa-gift me-2"></i>Benefícios
                        <span className="text-muted fw-normal small ms-2">o que a empresa paga a ele</span>
                      </h6>
                      {resumo.dados.beneficios.length === 0 ? (
                        <p className="text-muted small mb-4">Nenhum benefício cadastrado na Convenia.</p>
                      ) : (
                        <div className="table-responsive mb-4">
                          <table className="table table-sm table-striped align-middle mb-0">
                            <thead><tr>
                              <th>Benefício</th><th>Tipo</th><th>Operadora</th>
                              <th>Valor empresa</th><th>Desconto colaborador</th><th>Pagamento</th>
                            </tr></thead>
                            <tbody>
                              {resumo.dados.beneficios.map((b, i) => (
                                <tr key={i}>
                                  <td className="fw-semibold">{b.nome || '-'}</td>
                                  <td>{b.tipo || '-'}</td>
                                  <td>{b.operadora || '-'}</td>
                                  <td>{b.valor_empresa != null ? fmtBRL(b.valor_empresa) : '—'}</td>
                                  <td>{b.valor_colaborador != null ? fmtBRL(b.valor_colaborador) : '—'}</td>
                                  <td className="small text-muted">{(b.pagamento || '').replaceAll('-', ' ')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* ── férias ── */}
                      <h6 className="fw-bold mb-2" style={{ color: '#00205b' }}>
                        <i className="fas fa-umbrella-beach me-2"></i>Férias
                      </h6>
                      <div className="row g-3 mb-4">
                        <div className="col-md-6">
                          <div className="small text-muted mb-1">Períodos aquisitivos (saldo)</div>
                          {resumo.dados.ferias.periodos.length === 0
                            ? <p className="text-muted small">Nenhum período aquisitivo.</p>
                            : resumo.dados.ferias.periodos.map((p, i) => (
                              <div key={i} className="d-flex align-items-center gap-2 small mb-1">
                                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {p.aquisitivo_inicio} a {p.aquisitivo_fim}
                                </span>
                                <span className={`badge ${p.saldo > 0 ? 'bg-primary' : 'bg-light text-muted border'}`}>
                                  saldo {p.saldo ?? '—'}d
                                </span>
                                {p.concessivo_fim && p.saldo > 0 && (
                                  <span className="text-muted">gozar até {p.concessivo_fim}</span>
                                )}
                              </div>
                            ))}
                        </div>
                        <div className="col-md-6">
                          <div className="small text-muted mb-1">Férias tiradas / solicitadas</div>
                          {resumo.dados.ferias.solicitacoes.length === 0
                            ? <p className="text-muted small">Nenhuma solicitação de férias.</p>
                            : resumo.dados.ferias.solicitacoes.map((f, i) => (
                              <div key={i} className="d-flex align-items-center gap-2 small mb-1">
                                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {f.inicio} a {f.fim}
                                </span>
                                <span className="badge bg-light text-muted border">{f.dias}d</span>
                                {f.abono > 0 && <span className="badge bg-light text-muted border">+{f.abono}d abono</span>}
                                {f.decimo_terceiro && <span className="badge bg-light text-muted border">13º</span>}
                                {f.cancelada
                                  ? <span className="badge bg-danger">cancelada</span>
                                  : f.status === 'approved'
                                    ? <span className="badge bg-success">aprovada</span>
                                    : <span className="badge bg-warning text-dark">{f.status}</span>}
                              </div>
                            ))}
                        </div>
                      </div>

                      {/* ── histórico salarial ── */}
                      <h6 className="fw-bold mb-2" style={{ color: '#00205b' }}>
                        <i className="fas fa-money-bill-trend-up me-2"></i>Histórico salarial
                      </h6>
                      {resumo.dados.historico_salarial.length === 0 ? (
                        <p className="text-muted small mb-4">Sem histórico salarial no espelho.</p>
                      ) : (
                        <div className="table-responsive mb-4">
                          <table className="table table-sm table-striped align-middle mb-0">
                            <thead><tr>
                              <th>Período</th><th>Salário</th><th>Cargo</th><th>Motivo</th>
                            </tr></thead>
                            <tbody>
                              {[...resumo.dados.historico_salarial].reverse().map((h, i) => (
                                <tr key={i} className={h.ativo ? 'fw-semibold' : ''}>
                                  <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                    {h.de} {h.ate ? `a ${h.ate}` : '(vigente)'}
                                  </td>
                                  <td>{fmtBRL(h.salario)}</td>
                                  <td>{h.cargo || '-'}</td>
                                  <td className="small text-muted">{h.motivo || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* ── dependentes + afastamentos lado a lado ── */}
                      <div className="row g-3 mb-4">
                        <div className="col-md-6">
                          <h6 className="fw-bold mb-2" style={{ color: '#00205b' }}>
                            <i className="fas fa-people-roof me-2"></i>Dependentes
                          </h6>
                          {resumo.dados.dependentes.length === 0
                            ? <p className="text-muted small">Nenhum dependente cadastrado.</p>
                            : resumo.dados.dependentes.map((dp, i) => (
                              <div key={i} className="small mb-1">
                                <b>{dp.nome}</b>
                                {dp.relacao && <span className="text-muted"> · {dp.relacao}</span>}
                                {dp.nascimento && <span className="text-muted"> · nasc. {dp.nascimento}</span>}
                                {dp.ir && <span className="badge bg-light text-muted border ms-1">IR</span>}
                              </div>
                            ))}
                        </div>
                        <div className="col-md-6">
                          <h6 className="fw-bold mb-2" style={{ color: '#00205b' }}>
                            <i className="fas fa-notes-medical me-2"></i>Afastamentos e faltas
                          </h6>
                          {resumo.dados.afastamentos.length === 0
                            ? <p className="text-muted small">Nenhum afastamento registrado.</p>
                            : resumo.dados.afastamentos.map((a, i) => (
                              <div key={i} className="small mb-1">
                                <b>{a.motivo || a.tipo || 'Afastamento'}</b>
                                <span className="text-muted"> · {a.inicio}{a.fim ? ` a ${a.fim}` : ''}</span>
                                {a.descricao && <div className="text-muted">{a.descricao}</div>}
                              </div>
                            ))}
                        </div>
                      </div>

                      {/* ── linha do tempo ── */}
                      <h6 className="fw-bold mb-3" style={{ color: '#00205b' }}>
                        <i className="fas fa-timeline me-2"></i>Linha do tempo
                        <span className="text-muted fw-normal small ms-2">do mais recente ao mais antigo</span>
                      </h6>
                      <div style={{ borderLeft: '2px solid #e9ecef', marginLeft: 8, paddingLeft: 20 }}>
                        {resumo.dados.eventos.map((e, i) => {
                          const t = TIPO_EVENTO[e.tipo] || { icone: 'fa-circle', cor: '#6c757d' };
                          return (
                            <div key={i} className="mb-3" style={{ position: 'relative' }}>
                              <span style={{
                                position: 'absolute', left: -31, top: 2, width: 20, height: 20,
                                borderRadius: '50%', backgroundColor: '#fff',
                                border: `2px solid ${t.cor}`, display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                              }}>
                                <i className={`fas ${t.icone}`} style={{ fontSize: 9, color: t.cor }}></i>
                              </span>
                              <div className="small text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {e.data}
                              </div>
                              <div className="fw-semibold" style={{ fontSize: '.9rem' }}>{e.titulo}</div>
                              {e.detalhe && <div className="small text-muted">{e.detalhe}</div>}
                            </div>
                          );
                        })}
                        {resumo.dados.eventos.length === 0 && (
                          <p className="text-muted small">Nenhum evento registrado.</p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="modal-footer bg-light">
                  <button className="btn btn-secondary" onClick={() => setResumo(null)}>
                    <i className="fas fa-times me-2"></i>Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BpoRhColaboradores;
